import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";

import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { ThemeProvider } from "./contexts/ThemeContext";

import Navbar from "./components/Navbar";
import Footer from "./components/Footer";
import CookieConsent from "./components/CookieConsent";
import LangRouteSync from "./components/LangRouteSync";
import LanguageSuggestionBanner from "./components/LanguageSuggestionBanner";
import ProtectedRoute from "./components/ProtectedRoute";
import AnalyticsTracker from "./components/AnalyticsTracker";

import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import CityPage from "./pages/CityPage";
import { CITIES } from "./lib/cities";
import Dashboard from "./pages/Dashboard";
import RestaurantSettings from "./pages/RestaurantSettings";
import MenuBuilder from "./pages/MenuBuilder";
import ThemeCustomizer from "./pages/ThemeCustomizer";
import ThemePicker from "./pages/ThemePicker";
import QRGenerator from "./pages/QRGenerator";
import TableCards from "./pages/TableCards";
import Statistics from "./pages/Statistics";
import ServiceRequests from "./pages/ServiceRequests";
import Orders from "./pages/Orders";
import TelegramSettings from "./pages/TelegramSettings";
import AdminPanel from "./pages/AdminPanel";
import AdminSubscriptions from "./pages/AdminSubscriptions";
import PublicMenu from "./pages/PublicMenu";
import PaymentSuccess from "./pages/PaymentSuccess";
import Impressum from "./pages/Impressum";
import Datenschutz from "./pages/Datenschutz";
import DemoTurkish from "./pages/demos/DemoTurkish";
import DemoCafe from "./pages/demos/DemoCafe";
import DemoSteakhouse from "./pages/demos/DemoSteakhouse";

function App() {
  // Note: <title> is set dynamically by useLocalizedSEO on each page mount.
  // Do not override it here, otherwise it races with the page-level SEO hook.

  return (
    <div className="App">
      <ThemeProvider>
        <LanguageProvider>
          <AuthProvider>
            <BrowserRouter>
              <LangRouteSync />
              <AnalyticsTracker />
              <LanguageSuggestionBanner />
              <Navbar />
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/pricing" element={<Pricing />} />
                <Route path="/impressum" element={<Impressum />} />
                <Route path="/datenschutz" element={<Datenschutz />} />
                {/* Localized public routes — each language has fully indexable URLs */}
                {["de", "en", "tr"].flatMap((lng) => [
                  <Route key={`${lng}-home`} path={`/${lng}`} element={<Landing />} />,
                  <Route key={`${lng}-pricing`} path={`/${lng}/pricing`} element={<Pricing />} />,
                  <Route key={`${lng}-impressum`} path={`/${lng}/impressum`} element={<Impressum />} />,
                  <Route key={`${lng}-datenschutz`} path={`/${lng}/datenschutz`} element={<Datenschutz />} />,
                ])}
                {/* Programmatic City Pages — one URL per city (each in its country's primary language) */}
                {CITIES.map((c) => (
                  <Route key={`city-${c.id}`} path={c.path} element={<CityPage />} />
                ))}
                <Route path="/login" element={<Login />} />
                <Route path="/register" element={<Register />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/m/:slug" element={<PublicMenu />} />
                <Route path="/payment/success" element={<PaymentSuccess />} />
                <Route path="/demo-turkish-restaurant" element={<DemoTurkish />} />
                <Route path="/demo-cafe" element={<DemoCafe />} />
                <Route path="/demo-steakhouse" element={<DemoSteakhouse />} />
                <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id" element={<ProtectedRoute><RestaurantSettings /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/menu" element={<ProtectedRoute><MenuBuilder /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/theme" element={<ProtectedRoute><ThemeCustomizer /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/themes" element={<ProtectedRoute><ThemePicker /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/qr" element={<ProtectedRoute><QRGenerator /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/tables" element={<ProtectedRoute><TableCards /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/stats" element={<ProtectedRoute><Statistics /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/requests" element={<ProtectedRoute><ServiceRequests /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
                <Route path="/dashboard/restaurants/:id/telegram" element={<ProtectedRoute><TelegramSettings /></ProtectedRoute>} />
                <Route path="/dashboard/admin" element={<ProtectedRoute requireAdmin><AdminPanel /></ProtectedRoute>} />
                <Route path="/dashboard/admin/subscriptions" element={<ProtectedRoute requireAdmin><AdminSubscriptions /></ProtectedRoute>} />
              </Routes>
              <Footer />
              <CookieConsent />
              <Toaster richColors position="top-right" />
            </BrowserRouter>
          </AuthProvider>
        </LanguageProvider>
      </ThemeProvider>
    </div>
  );
}

export default App;
